import { MatterContactConnectionDescriptionIcon } from "@/components/features/matter/information/MatterContactConnectionDescriptionIcon";
import {
  Button,
  ContactCallSplitButton,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type {
  ConnectionContactPreview,
  Connection,
  Contact,
  MatterContactConnection,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { copyToClipboard } from "@/lib/utils/copyToClipboard";
import {
  formatAddress,
  formatContactsName,
  formatLocalOrIntlPhoneNumber,
} from "@/lib/utils/format";
import { hasNonEmptyTrimmedString } from "@/lib/utils/formFieldCoercion";
import { getContactHeroTypeIcon } from "@/lib/utils/iconUtils";
import {
  MATTER_CONTACT_CONNECTION_RICH_CARD_WHITE_PANEL_CLASS,
  MATTER_CONTACT_HERO_TONE_LEAD_ICON_CLASS,
  matterContactHeroToneGradientPanelClass,
  type MatterContactHeroTone,
} from "@/lib/utils/matters/matter-contact-connection-hero-tone";
import { getlabel } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import type { LucideIcon } from "lucide-react";
import {
  ChevronRight,
  Copy,
  Mail,
  MailWarning,
  MapPin,
  MessageSquare,
  Pencil,
  Phone,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  ContactPortalStatusIcon,
  getContactPortalEmail,
} from "./ContactPortalStatusIcon";

export type MatterContactConnectionRichCardVisualVariant = "hero" | "white";

const RICH_DETAILS_HIDDEN_STORAGE_KEY_PREFIX =
  "matter-contact-connection-rich-details-hidden";

type ContactWithConnectionMeta = (Contact | ConnectionContactPreview) & {
  connectionId?: string;
  contactName?: string;
  description?: string | null;
  label?: string | null;
};

type RichCardContactInput = ContactWithConnectionMeta | null;

type ConnectionPhone = Contact["phones"][number];
type ConnectionEmail = Contact["emails"][number];

const asTrimmedString = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

type MatterContactConnectionRichCardBaseProps = {
  /**
   * Complete contact data, or `null` when there is no contact (e.g. unknown medical provider).
   * Data fetching belongs to the caller.
   */
  contact: RichCardContactInput;
  /** Optional matter-specific metadata for the supplied contact. */
  matterContactConnection?: Connection | MatterContactConnection;
  /** When set, wins over formatted / denormalized names from `contact`. */
  displayName?: string;
  /** When there is no usable name */
  emptyNameLabel?: string;
  /** Optional chips under the title (e.g. medical treatment types) */
  badges?: string[];
  highlightedBadges?: string[];
  /** Optional content rendered beside the contact name. */
  titleAccessory?: ReactNode;
  /** Max chips to show (counts `badges` and `highlightedBadges` together) before "+N". */
  maxVisibleBadges?: number;
  /** Overrides the icon derived from `contact.type`. */
  leadIcon?: LucideIcon;
  onEdit?: () => void;
  showEditButton?: boolean;
  /** Overrides the default contact-update permission gate for callers editing the owning record. */
  editPermissionOverride?: boolean;
  editAriaLabel?: string;
  editButtonTestId?: string;
  /**
   * Remove this matter-contact connection. Receives the supplied connection id.
   * Gated by `matterContactConnections` delete permission and `disableDelete`.
   */
  onDelete?: (matterContactConnectionId: string) => void;
  disableDelete?: boolean;
  /** Defaults to the same test id as `MatterClientHeroPreviewCard` delete. */
  deleteButtonTestId?: string;
  onClearSelection?: () => void;
  /**
   * Phones shown in the expandable "Contact details" block.
   * Defaults to all phones on the contact (Call menu always lists all phones).
   */
  detailPhones?: Contact["phones"];
  /**
   * Emails shown in the expandable "Contact details" block.
   * Defaults to all emails on the contact.
   */
  detailEmails?: Contact["emails"];
  /** Visual variant — same finite set as `MatterContactHeroTone` (client green, primary gray, defendant blue, provider purple). */
  tone?: MatterContactHeroTone;
  /**
   * `hero` (default): gradient panel keyed by `tone`.
   * `white`: white background and gray border for secondary roles in split modals.
   */
  visualVariant?: MatterContactConnectionRichCardVisualVariant;
  /** Extra content below contact details inside the expanded section */
  expandedFooter?: ReactNode;
  /** Show contact-level individual status badges such as Minor or Deceased. */
  showIndividualStatusBadges?: boolean;
  /** Hides the matter–contact connection description icon (e.g. compact modal preview). */
  hideConnectionDescription?: boolean;
  testId?: string;
  className?: string;
  innerClassName?: string;
};

type MatterContactConnectionRichCardCommunicationProps =
  | {
      communicationActions?: "call-email";
      matterId?: string;
    }
  | {
      communicationActions: "call-text";
      matterId: string;
    };

export type MatterContactConnectionRichCardProps =
  MatterContactConnectionRichCardBaseProps &
    MatterContactConnectionRichCardCommunicationProps;

export function MatterContactConnectionRichCard({
  contact,
  matterContactConnection,
  displayName: displayNameProp,
  emptyNameLabel = "Unknown contact",
  badges,
  highlightedBadges,
  titleAccessory,
  maxVisibleBadges = 3,
  leadIcon: LeadIconProp,
  onEdit,
  showEditButton = false,
  editPermissionOverride,
  editAriaLabel = "Edit contact",
  editButtonTestId,
  onDelete,
  disableDelete,
  deleteButtonTestId = "contact-preview-chip-delete",
  onClearSelection,
  communicationActions = "call-email",
  matterId,
  detailPhones,
  detailEmails,
  expandedFooter,
  showIndividualStatusBadges = false,
  hideConnectionDescription = false,
  tone = "provider",
  visualVariant = "hero",
  testId,
  className,
  innerClassName,
}: MatterContactConnectionRichCardProps) {
  const isWhiteVariant = visualVariant === "white";
  const { hasActionPermission } = useUser();
  const { openCommunicationModal } = useGlobalModal();
  const contactIdString = asTrimmedString(contact?.id);
  const connectionIdString = asTrimmedString(
    matterContactConnection?.id ?? contact?.connectionId,
  );
  const connectionLabelString = asTrimmedString(
    (matterContactConnection ? getlabel(matterContactConnection) : undefined) ??
      contact?.label,
  );

  const detailsHiddenStorageKey = useMemo(
    () =>
      [
        RICH_DETAILS_HIDDEN_STORAGE_KEY_PREFIX,
        matterId?.trim() || "no-matter",
        connectionLabelString.toLowerCase().replace(/\s+/g, "-") || "no-label",
        connectionIdString || "no-connection",
        contactIdString || "no-contact",
      ].join(":"),
    [connectionIdString, connectionLabelString, contactIdString, matterId],
  );

  const [detailsHidden, setDetailsHidden] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    const storedValue = window.localStorage.getItem(detailsHiddenStorageKey);
    if (storedValue == null) {
      return false;
    }
    return storedValue === "true";
  });

  const canEditContactDetails = hasActionPermission(
    ACTION_PERMISSION_KEYS.contact,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDeleteMatterContactConnection = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const canViewContactCommunications = Boolean(true);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const storedValue = window.localStorage.getItem(detailsHiddenStorageKey);
    if (storedValue == null) {
      setDetailsHidden(false);
      return;
    }
    setDetailsHidden(storedValue === "true");
  }, [detailsHiddenStorageKey]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    window.localStorage.setItem(detailsHiddenStorageKey, String(detailsHidden));
  }, [detailsHidden, detailsHiddenStorageKey]);

  const resolvedContact = contact;

  const resolvedName = (() => {
    const c = resolvedContact;
    if (displayNameProp?.trim()) return displayNameProp.trim();
    if (!c) return "";
    const formatted = formatContactsName(
      c as Parameters<typeof formatContactsName>[0],
    )?.trim();
    if (formatted) return formatted;
    if ("organizationName" in c) {
      const org = c.organizationName;
      if (typeof org === "string" && org.trim() !== "") return org.trim();
    }
    if ("groupName" in c) {
      const group = c.groupName;
      if (typeof group === "string" && group.trim() !== "") return group.trim();
    }
    if ("contactName" in c && typeof c.contactName === "string") {
      return c.contactName.trim();
    }
    return "";
  })();

  const allPhones = resolvedContact?.phones ?? [];
  const allEmails = resolvedContact?.emails ?? [];

  const phonesForDetail = detailPhones ?? allPhones;
  const emailsForDetail = detailEmails ?? allEmails;
  const portalAccess = resolvedContact?.portalAccess;
  const portalEmail = getContactPortalEmail(portalAccess);
  const portalEmailMissingFromDetails =
    portalEmail !== "" &&
    !emailsForDetail.some(
      (email) =>
        email.address?.trim().toLowerCase() === portalEmail.toLowerCase(),
    );
  const portalEmailMissingFromContact =
    portalEmail !== "" &&
    !allEmails.some(
      (email) =>
        email.address?.trim().toLowerCase() === portalEmail.toLowerCase(),
    );

  const addresses = resolvedContact?.addresses ?? [];

  const DefaultIcon = getContactHeroTypeIcon(resolvedContact?.type);
  const LeadIcon = LeadIconProp ?? DefaultIcon;

  const matterConnectionLabelRaw =
    (matterContactConnection ? getlabel(matterContactConnection) : undefined) ??
    contact?.label ??
    "";
  const matterConnectionLabelDisplay =
    matterConnectionLabelRaw.trim() !== ""
      ? matterConnectionLabelRaw.trim()
      : null;

  const combinedBadges = useMemo(() => {
    const entries: {
      key: string;
      label: string;
      tone: "default" | "highlighted" | "warning" | "critical";
    }[] = [];
    let i = 0;
    if (resolvedContact?.organizationMedicalProviderAcceptsLiens) {
      entries.push({
        key: "medical-provider-accepts-liens",
        label: "Accepts liens",
        tone: "highlighted",
      });
    }
    for (const label of badges ?? []) {
      entries.push({
        key: `b-${i}-${label}`,
        label,
        tone: "default",
      });
      i += 1;
    }
    for (const label of highlightedBadges ?? []) {
      entries.push({
        key: `h-${i}-${label}`,
        label,
        tone: "highlighted",
      });
      i += 1;
    }
    if (showIndividualStatusBadges && resolvedContact?.type === "individual") {
      if (
        "individualMinor" in resolvedContact &&
        resolvedContact.individualMinor
      ) {
        entries.push({
          key: "status-minor",
          label: "Minor",
          tone: "warning",
        });
      }
      if (
        "individualDeceased" in resolvedContact &&
        resolvedContact.individualDeceased
      ) {
        entries.push({
          key: "status-deceased",
          label: "Deceased",
          tone: "critical",
        });
      }
    }
    return entries;
  }, [badges, highlightedBadges, resolvedContact, showIndividualStatusBadges]);

  const visibleBadgeEntries = combinedBadges.slice(0, maxVisibleBadges);
  const hiddenBadgeEntries = combinedBadges.slice(maxVisibleBadges);

  const hasAnyBadges = combinedBadges.length > 0;
  const hasConnectionDescription =
    !hideConnectionDescription &&
    Boolean(
      (matterContactConnection?.description ?? contact?.description)?.trim(),
    );
  const showBadgeOrMetadataRow = hasAnyBadges || hasConnectionDescription;

  const hasDetailBody =
    phonesForDetail.length > 0 ||
    emailsForDetail.length > 0 ||
    portalEmailMissingFromDetails ||
    addresses.length > 0 ||
    expandedFooter != null;

  const showContactDetailsSection =
    phonesForDetail.length > 0 ||
    emailsForDetail.length > 0 ||
    portalEmailMissingFromDetails ||
    addresses.length > 0 ||
    expandedFooter != null;

  const gradientPanelClass: string =
    matterContactHeroToneGradientPanelClass(tone);
  const leadIconAccentClass: string =
    MATTER_CONTACT_HERO_TONE_LEAD_ICON_CLASS[tone];

  const panelShellClass = isWhiteVariant
    ? cn(
        MATTER_CONTACT_CONNECTION_RICH_CARD_WHITE_PANEL_CLASS,
        "rounded-2xl",
        innerClassName,
      )
    : cn(gradientPanelClass, "rounded-2xl shadow-none", innerClassName);

  const leadIconTileClass = isWhiteVariant
    ? "border-border/70 bg-muted/40 border"
    : "bg-white shadow-sm";

  const nameTextClass = isWhiteVariant ? "text-foreground" : "text-white";
  const roleLabelTextClass = isWhiteVariant
    ? "text-muted-foreground"
    : "text-white/65";
  const headerActionClass = isWhiteVariant
    ? "text-muted-foreground transition-colors hover:text-foreground"
    : "text-white/70 transition-colors hover:text-white";
  const badgeClass = isWhiteVariant
    ? "rounded-full border border-border bg-muted/50 px-2 py-0.5 text-xs text-foreground"
    : "rounded-full border border-white/40 bg-white/20 px-2 py-0.5 text-xs text-white";
  const badgeHighlightedClass = isWhiteVariant
    ? "rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-900 dark:border-amber-400/30 dark:bg-amber-500/15 dark:text-amber-200"
    : "rounded-full border border-amber-200/30 bg-amber-400/15 px-2 py-0.5 text-xs text-amber-100";
  const badgeWarningClass = isWhiteVariant
    ? "rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-900 dark:border-amber-400/30 dark:bg-amber-500/15 dark:text-amber-200"
    : "rounded-full border border-amber-200/30 bg-amber-400/15 px-2 py-0.5 text-xs text-amber-100";
  const badgeCriticalClass = isWhiteVariant
    ? "rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs text-red-700 dark:border-red-400/30 dark:bg-red-500/15 dark:text-red-200"
    : "rounded-full border border-red-200/30 bg-red-400/15 px-2 py-0.5 text-xs text-red-100";
  const connectionDescriptionWrapperClass = isWhiteVariant
    ? "inline-flex items-center"
    : "inline-flex items-center [&_.text-muted-foreground]:text-white/70";
  const commTriggerClassName = isWhiteVariant
    ? "h-7 text-xs"
    : "h-7 border-white/30 bg-white/10 text-xs text-white hover:bg-white/20 hover:text-white";
  const detailsToggleClass = isWhiteVariant
    ? "text-muted-foreground hover:text-foreground"
    : "text-white/70 hover:text-white";
  const detailsSectionClass = isWhiteVariant
    ? "border-border text-foreground"
    : "border-white/20 text-white/80";
  const detailsHeadingClass = isWhiteVariant
    ? "text-foreground"
    : "text-white/90";
  const detailsIconClass = isWhiteVariant
    ? "text-muted-foreground"
    : "text-white/60";
  const detailsCopyButtonClass = isWhiteVariant
    ? "text-muted-foreground hover:text-foreground"
    : "text-white/50 hover:text-white/90";
  const detailsEmptyTextClass = isWhiteVariant
    ? "text-muted-foreground italic"
    : "text-white/50 italic";

  const showEditControl =
    showEditButton &&
    Boolean(onEdit) &&
    (editPermissionOverride ??
      (Boolean(resolvedContact) && canEditContactDetails));
  const matterContactConnectionIdForDelete = connectionIdString;

  const showDeleteControl =
    matterContactConnectionIdForDelete !== "" &&
    Boolean(onDelete) &&
    !disableDelete &&
    canDeleteMatterContactConnection;

  const showClearSelectionControl = Boolean(onClearSelection);

  const hasCallablePhone = allPhones.some((p) =>
    hasNonEmptyTrimmedString(p.number),
  );
  const canText =
    hasCallablePhone &&
    canViewContactCommunications &&
    Boolean(
      resolvedContact?.id ??
        (resolvedContact as { contactId?: string } | undefined)?.contactId,
    );

  return (
    <div
      className={cn("border-border w-[380px] shrink-0 border-r p-6", className)}
      data-testid={testId}
    >
      <div className={panelShellClass}>
        <div className="mb-3 flex items-start gap-3">
          <div
            className={cn(
              "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl",
              leadIconTileClass,
            )}
          >
            <LeadIcon className={cn("h-6 w-6", leadIconAccentClass)} />
          </div>
          <div className="flex min-h-12 min-w-0 flex-1 flex-col justify-center gap-0.5 text-left">
            <div className="flex min-w-0 items-center gap-1.5">
              <TooltipWrapper
                message={
                  <span className="flex items-center gap-2">
                    <span className="min-w-0 break-words select-text">
                      {resolvedName || emptyNameLabel}
                    </span>
                    {resolvedName ? (
                      <button
                        type="button"
                        aria-label="Copy full name"
                        className="hover:bg-primary/10 focus-visible:ring-ring shrink-0 rounded p-1 focus-visible:ring-2 focus-visible:outline-none"
                        onClick={(event) => {
                          event.stopPropagation();
                          void copyToClipboard(resolvedName, "Name");
                        }}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </span>
                }
              >
                <span
                  className={cn(
                    "text-md min-w-0 truncate leading-tight font-semibold",
                    nameTextClass,
                  )}
                  data-testid="contact-preview-chip-name"
                >
                  {resolvedName || emptyNameLabel}
                </span>
              </TooltipWrapper>
              {titleAccessory}
              <ContactPortalStatusIcon
                portalAccess={resolvedContact?.portalAccess}
                isWhiteVariant={isWhiteVariant}
              />
            </div>
            {matterConnectionLabelDisplay ? (
              <span className={cn("text-sm leading-tight", roleLabelTextClass)}>
                {matterConnectionLabelDisplay}
              </span>
            ) : null}
          </div>
          {showEditControl || showDeleteControl || showClearSelectionControl ? (
            <div className="flex shrink-0 items-start gap-1">
              {showEditControl ? (
                <button
                  type="button"
                  onClick={onEdit}
                  className={headerActionClass}
                  aria-label={editAriaLabel}
                  data-testid={editButtonTestId}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              ) : null}
              {showDeleteControl ? (
                <button
                  type="button"
                  onClick={() => {
                    onDelete?.(matterContactConnectionIdForDelete);
                  }}
                  className={headerActionClass}
                  aria-label="Remove contact connection"
                  data-testid={deleteButtonTestId}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
              {showClearSelectionControl ? (
                <button
                  type="button"
                  onClick={onClearSelection}
                  className={headerActionClass}
                  aria-label="Remove contact"
                  data-testid={deleteButtonTestId}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        {showBadgeOrMetadataRow ? (
          <div className="mb-3 flex flex-wrap items-center gap-1 gap-x-2">
            {visibleBadgeEntries.map((entry) => (
              <span
                key={entry.key}
                className={
                  entry.tone === "critical"
                    ? badgeCriticalClass
                    : entry.tone === "warning"
                      ? badgeWarningClass
                      : entry.tone === "highlighted"
                        ? badgeHighlightedClass
                        : badgeClass
                }
              >
                {entry.label}
              </span>
            ))}
            {hiddenBadgeEntries.length > 0 ? (
              <TooltipWrapper
                message={hiddenBadgeEntries.map((e) => e.label).join(", ")}
              >
                <span className={badgeClass}>+{hiddenBadgeEntries.length}</span>
              </TooltipWrapper>
            ) : null}
            {hasConnectionDescription ? (
              <div className={connectionDescriptionWrapperClass}>
                <MatterContactConnectionDescriptionIcon
                  description={
                    matterContactConnection?.description ?? contact?.description
                  }
                />
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <ContactCallSplitButton
            className={commTriggerClassName}
            contactId={contactIdString || undefined}
            phones={allPhones}
            size="sm"
          />

          {communicationActions === "call-text" ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className={commTriggerClassName}
              disabled={!canText || !matterId?.trim()}
              onClick={() => {
                if (!canText || !matterId?.trim() || !resolvedContact?.id) {
                  return;
                }
                void openCommunicationModal({
                  contactId: resolvedContact.id,
                  matterId: matterId.trim(),
                });
              }}
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Text
            </Button>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className={commTriggerClassName}
                >
                  <Mail className="h-3.5 w-3.5" />
                  Email
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[220px]">
                {allEmails.length === 0 ? (
                  <DropdownMenuItem disabled>
                    No emails on file
                  </DropdownMenuItem>
                ) : (
                  allEmails.map((email: ConnectionEmail, i: number) => (
                    <DropdownMenuItem key={i} asChild>
                      <a
                        href={`mailto:${email.address}`}
                        className="flex items-center gap-2"
                      >
                        <Mail className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
                        <span className="flex-1 truncate">
                          {email.label ? `${email.label}: ` : ""}
                          {email.address}
                        </span>
                      </a>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <button
            type="button"
            onClick={() => {
              setDetailsHidden((p) => !p);
            }}
            className={cn(
              "ml-auto flex items-center gap-0.5 text-xs transition-colors",
              detailsToggleClass,
            )}
          >
            {detailsHidden ? "Details" : "Hide"}
            <ChevronRight
              className={cn(
                "h-3 w-3 transition-transform",
                !detailsHidden && "rotate-90",
              )}
            />
          </button>
        </div>

        {!detailsHidden ? (
          <div
            className={cn(
              "mt-3 space-y-4 border-t pt-3 text-xs",
              detailsSectionClass,
            )}
          >
            {hasDetailBody ? (
              <>
                {showContactDetailsSection ? (
                  <div className="space-y-2">
                    <p className={cn("font-semibold", detailsHeadingClass)}>
                      Contact details
                    </p>
                    {phonesForDetail.map(
                      (phone: ConnectionPhone, i: number) => (
                        <div
                          key={`p-${i}`}
                          className={cn(
                            "flex items-center gap-2",
                            detailsSectionClass,
                          )}
                        >
                          <Phone
                            className={cn("h-3 w-3 shrink-0", detailsIconClass)}
                          />
                          <span className="flex-1">
                            {phone.label ? `${phone.label}: ` : ""}
                            {formatLocalOrIntlPhoneNumber(phone)}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              void copyToClipboard(
                                formatLocalOrIntlPhoneNumber(phone),
                                "Phone number",
                              );
                            }}
                            className={detailsCopyButtonClass}
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        </div>
                      ),
                    )}
                    {emailsForDetail.map(
                      (email: ConnectionEmail, i: number) => (
                        <div
                          key={`e-${i}`}
                          className={cn(
                            "flex items-center gap-2",
                            detailsSectionClass,
                          )}
                        >
                          <Mail
                            className={cn("h-3 w-3 shrink-0", detailsIconClass)}
                          />
                          <span className="flex-1 truncate">
                            {email.address ?? ""}
                          </span>
                          {portalEmail !== "" &&
                          email.address?.trim().toLowerCase() ===
                            portalEmail.toLowerCase() ? (
                            <ContactPortalStatusIcon
                              portalAccess={portalAccess}
                              isWhiteVariant={isWhiteVariant}
                            />
                          ) : null}
                          <button
                            type="button"
                            onClick={() => {
                              void copyToClipboard(
                                email.address ?? "",
                                "Email",
                              );
                            }}
                            className={detailsCopyButtonClass}
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        </div>
                      ),
                    )}
                    {portalEmailMissingFromDetails ? (
                      <div
                        role={
                          portalEmailMissingFromContact ? "status" : undefined
                        }
                        className={cn(
                          "flex items-start gap-2",
                          portalEmailMissingFromContact &&
                            (isWhiteVariant
                              ? "text-amber-700"
                              : "text-amber-200"),
                        )}
                      >
                        {portalEmailMissingFromContact ? (
                          <MailWarning
                            className="mt-0.5 h-3.5 w-3.5 shrink-0"
                            aria-hidden="true"
                          />
                        ) : (
                          <ContactPortalStatusIcon
                            portalAccess={portalAccess}
                            isWhiteVariant={isWhiteVariant}
                          />
                        )}
                        <span className="min-w-0 flex-1">
                          {portalAccess?.activeConnection
                            ? "Portal account email"
                            : "Portal invitation email"}{" "}
                          <span className="font-medium break-all">
                            {portalEmail}
                          </span>
                          {portalEmailMissingFromContact
                            ? " is not saved on this contact."
                            : null}
                        </span>
                        {portalEmailMissingFromContact ? (
                          <ContactPortalStatusIcon
                            portalAccess={portalAccess}
                            isWhiteVariant={isWhiteVariant}
                          />
                        ) : null}
                        <button
                          type="button"
                          onClick={() => {
                            void copyToClipboard(portalEmail, "Email");
                          }}
                          className={detailsCopyButtonClass}
                          aria-label="Copy contact portal email"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                    ) : null}
                    {addresses.map((addr, i) => {
                      const formatted = formatAddress({
                        street1: addr.street1 ?? undefined,
                        street2: addr.street2 ?? undefined,
                        city: addr.city ?? undefined,
                        state: addr.state ?? "",
                        zipCode: addr.zipCode ?? undefined,
                        country:
                          addr.country === "United States"
                            ? undefined
                            : (addr.country ?? undefined),
                      });
                      if (!formatted) return null;
                      return (
                        <div
                          key={i}
                          className={cn(
                            "flex items-start gap-2",
                            detailsSectionClass,
                          )}
                        >
                          <MapPin
                            className={cn(
                              "mt-0.5 h-3 w-3 shrink-0",
                              detailsIconClass,
                            )}
                          />
                          <span>{formatted}</span>
                        </div>
                      );
                    })}
                    {phonesForDetail.length === 0 &&
                    emailsForDetail.length === 0 &&
                    !portalEmailMissingFromDetails &&
                    addresses.length === 0 ? (
                      <p className={detailsEmptyTextClass}>
                        No contact details on file
                      </p>
                    ) : null}
                  </div>
                ) : null}

                {expandedFooter}
              </>
            ) : (
              <p className={detailsEmptyTextClass}>
                No additional details on file.
              </p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
